import type { RenderCallbackContent } from '../types/cell-content'

// Cache for the Svelte module - loaded lazily
let svelteModule: { mount?: any; unmount?: any } | null | undefined = undefined

/**
 * Options for creating a Svelte cell
 */
export interface SvelteCellOptions {
  /**
   * Svelte component to render (works with both Svelte 4 and 5)
   * - Svelte 4: Class-based component (SvelteComponent)
   * - Svelte 5: Function-based component (Component)
   */
  component: any

  /**
   * Props to pass to the component
   */
  props?: Record<string, any>

  /**
   * Event listeners to attach to the component
   * Uses Svelte's event dispatching system ($on for v4, events option for v5)
   * @example
   * {
   *   click: (e) => console.log('clicked', e),
   *   change: (value) => console.log('changed', value)
   * }
   */
  on?: Record<string, (...args: any[]) => void>

  /**
   * Optional context to pass to the component
   * Accepts either a Map or a plain object (converted to Map internally)
   */
  context?: Map<any, any> | Record<string, any>
}

/**
 * Detect if a component is a Svelte 5 component
 *
 * Svelte 5 components are plain functions, while Svelte 4 components
 * are classes with prototype methods like $$set
 */
function isSvelte5Component(component: any): boolean {
  // Svelte 4 class components have $$set on prototype
  // Svelte 5 function components do not have this
  return (
    typeof component === 'function' &&
    !component.prototype?.$$set &&
    !component.prototype?.$destroy
  )
}

/**
 * Convert a plain object context to a Map
 */
function normalizeContext(
  context?: Map<any, any> | Record<string, any>,
): Map<any, any> | undefined {
  if (!context) return undefined
  if (context instanceof Map) return context
  return new Map(Object.entries(context))
}

/**
 * Create a datatable cell that renders a Svelte component
 *
 * This helper manages the Svelte component lifecycle, mounting and unmounting
 * the component when the cell is added/removed from the table.
 *
 * Supports both Svelte 4 (class-based) and Svelte 5 (function-based) components
 * with automatic version detection.
 *
 * @param options - Svelte cell options
 * @returns Render callback content for the datatable
 *
 * @example
 * ```typescript
 * import { createSvelteCell } from 'dap-design-system/helpers/svelte'
 * import StatusBadge from './StatusBadge.svelte'
 *
 * const columns = [
 *   {
 *     id: 'status',
 *     header: 'Status',
 *     cell: (row) => createSvelteCell({
 *       component: StatusBadge,
 *       props: {
 *         status: row.status,
 *         variant: 'success'
 *       },
 *       on: {
 *         click: () => console.log('Badge clicked')
 *       }
 *     })
 *   }
 * ]
 * ```
 */
export function createSvelteCell(
  options: SvelteCellOptions,
): RenderCallbackContent
export function createSvelteCell(
  component: any,
  props?: Record<string, any>,
): RenderCallbackContent

export function createSvelteCell(
  componentOrOptions: any | SvelteCellOptions,
  propsArg?: Record<string, any>,
): RenderCallbackContent {
  // Normalize arguments - check if first arg is SvelteCellOptions
  const isOptions = (
    arg: any | SvelteCellOptions,
  ): arg is SvelteCellOptions => {
    return (
      typeof arg === 'object' &&
      arg !== null &&
      'component' in arg &&
      typeof arg.component !== 'undefined'
    )
  }

  const options: SvelteCellOptions = isOptions(componentOrOptions)
    ? componentOrOptions
    : {
        component: componentOrOptions,
        props: propsArg,
      }

  const { component, props, on, context } = options

  // Track the Svelte component instance and version
  let instance: any = null
  let isSvelte5 = false

  // Helper to mount Svelte 4 component
  const mountSvelte4 = (
    container: HTMLElement,
    normalizedContext: Map<any, any> | undefined,
  ) => {
    // Svelte 4: Use new Component() constructor
    const mountOptions: Record<string, any> = {
      target: container,
      props: props || {},
    }

    if (normalizedContext) {
      mountOptions.context = normalizedContext
    }

    instance = new component(mountOptions)

    // Attach event listeners using $on
    if (on && instance) {
      Object.entries(on).forEach(([event, handler]) => {
        if (typeof instance.$on === 'function') {
          instance.$on(event, handler)
        }
      })
    }
  }

  // Get Svelte 5 API from cache
  const getSvelte5Api = (): { mount?: any; unmount?: any } | null => {
    if (svelteModule !== undefined) {
      return svelteModule
    }
    return null
  }

  // Try to load Svelte module asynchronously and cache it
  const loadSvelteModule = async (): Promise<{
    mount?: any
    unmount?: any
  } | null> => {
    if (svelteModule !== undefined) {
      return svelteModule
    }
    try {
      // Use Function constructor to create dynamic import that bypasses Vite's static analysis
      // This allows the code to work in both test environments (where svelte isn't installed)
      // and runtime environments (where svelte is available from the consuming project)
      const dynamicImport = new Function(
        'moduleName',
        'return import(moduleName)',
      )
      const mod = await dynamicImport('svelte')
      if (
        mod &&
        typeof mod.mount === 'function' &&
        typeof mod.unmount === 'function'
      ) {
        svelteModule = mod
        return mod
      }
    } catch {
      // Svelte 5 not available
    }
    svelteModule = null
    return null
  }

  // Internal render implementation
  const doRender = (
    container: HTMLElement,
    svelteApi: { mount?: any; unmount?: any } | null,
  ) => {
    const normalizedContext = normalizeContext(context)
    isSvelte5 = isSvelte5Component(component)

    if (isSvelte5 && svelteApi) {
      // Svelte 5: Use mount() function
      const mountOptions: Record<string, any> = {
        target: container,
        props: props || {},
      }

      if (normalizedContext) {
        mountOptions.context = normalizedContext
      }

      instance = svelteApi.mount(component, mountOptions)
    } else if (!isSvelte5) {
      // Svelte 4: Use class constructor
      mountSvelte4(container, normalizedContext)
    } else {
      // Svelte 5 component but no svelte module available
      throw new Error(
        'Svelte 5 component detected but svelte module not available',
      )
    }
  }

  return {
    render: (container: HTMLElement) => {
      try {
        isSvelte5 = isSvelte5Component(component)

        if (isSvelte5) {
          // Check if Svelte module is already cached
          const cachedApi = getSvelte5Api()
          if (cachedApi) {
            doRender(container, cachedApi)
          } else {
            // Load Svelte module asynchronously, then render
            loadSvelteModule().then(svelteApi => {
              try {
                doRender(container, svelteApi)
              } catch (error) {
                console.error(
                  'Error mounting Svelte component in datatable cell:',
                  error,
                )
                container.textContent = 'Error rendering cell'
              }
            })
          }
        } else {
          // Svelte 4: render synchronously
          doRender(container, null)
        }
      } catch (error) {
        console.error(
          'Error mounting Svelte component in datatable cell:',
          error,
        )
        container.textContent = 'Error rendering cell'
      }
    },

    cleanup: () => {
      if (instance !== null) {
        try {
          if (isSvelte5) {
            // Svelte 5: Use unmount() function
            const svelteApi = getSvelte5Api()
            if (svelteApi && typeof svelteApi.unmount === 'function') {
              svelteApi.unmount(instance)
            } else if (typeof instance.$destroy === 'function') {
              // Fallback to $destroy if unmount not available
              instance.$destroy()
            }
          } else {
            // Svelte 4: Use instance.$destroy()
            if (typeof instance.$destroy === 'function') {
              instance.$destroy()
            }
          }
        } catch (error) {
          console.error(
            'Error unmounting Svelte component in datatable cell:',
            error,
          )
        } finally {
          instance = null
        }
      }
    },
  }
}

/**
 * Type helper for creating type-safe cell renderers
 *
 * @example
 * ```typescript
 * import type { SvelteCellRenderer } from 'dap-design-system/helpers/svelte'
 *
 * interface User {
 *   id: number
 *   status: string
 * }
 *
 * const statusCell: SvelteCellRenderer<User> = (row) => createSvelteCell({
 *   component: StatusBadge,
 *   props: { status: row.status }
 * })
 * ```
 */
export type SvelteCellRenderer<TData = any> = (
  row: TData,
) => RenderCallbackContent
